/**
 * LabelMap domain object extending Volume with structure metadata.
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import type { ILabelMapStructure } from './types';
import { Volume } from './volume';

/**
 * Interface for a LabelMap domain object.
 */
export interface ILabelMap {
    /** Lookup key used by client */
    readonly key: string;
    /** Segmentation image data */
    readonly imageData: vtkImageData;
    /** Structure definitions */
    readonly structures: readonly ILabelMapStructure[];
}

/**
 * LabelMap domain object extending Volume with structure metadata.
 * A LabelMap is a Volume where voxel values represent labeled regions.
 */
export class LabelMap extends Volume implements ILabelMap {
    private readonly structuresByValue: Map<number, ILabelMapStructure>;
    private readonly structuresByName: Map<string, ILabelMapStructure>;
    private readonly structuresByMesh: Map<string, ILabelMapStructure>;

    constructor(
        key: string,
        imageData: vtkImageData,
        readonly structures: readonly ILabelMapStructure[]
    ) {
        super(key, imageData);

        // Build lookup maps for efficient structure access
        this.structuresByValue = new Map();
        this.structuresByName = new Map();
        this.structuresByMesh = new Map();

        for (const structure of structures) {
            this.structuresByValue.set(structure.labelValue, structure);
            this.structuresByName.set(structure.name, structure);
            if (structure.mesh) {
                this.structuresByMesh.set(structure.mesh, structure);
            }
        }
    }

    /**
     * Get structure by its label value.
     */
    getStructureByValue(labelValue: number): ILabelMapStructure | undefined {
        return this.structuresByValue.get(labelValue);
    }

    /**
     * Get structure by its name.
     */
    getStructureByName(name: string): ILabelMapStructure | undefined {
        return this.structuresByName.get(name);
    }

    /**
     * Get structure by its associated mesh key.
     */
    getStructureByMeshKey(meshKey: string): ILabelMapStructure | undefined {
        return this.structuresByMesh.get(meshKey);
    }

    /**
     * Get all label values present in this label map.
     */
    getLabelValues(): readonly number[] {
        return this.structures.map((s) => s.labelValue);
    }

    /**
     * Get all structure names.
     */
    getStructureNames(): readonly string[] {
        return this.structures.map((s) => s.name);
    }

    /**
     * Get all mesh keys associated with structures.
     */
    getMeshKeys(): readonly string[] {
        return this.structures.filter((s) => s.mesh !== undefined).map((s) => s.mesh!);
    }

    /**
     * Check if a structure with the given label value exists.
     */
    hasStructure(labelValue: number): boolean {
        return this.structuresByValue.has(labelValue);
    }

    /**
     * Get the number of defined structures.
     */
    get structureCount(): number {
        return this.structures.length;
    }
}
